import { ReactNode, useEffect, useMemo } from 'react';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { NextSeo } from 'next-seo';
import { LinkTag, NextSeoProps } from 'next-seo/lib/types';

import auth from '@udacity/ureact-hoth';

import { PageCategory } from '~/features/analytics/constants';
import { getGeoAnalyticsProperties } from '~/features/analytics/utils/get-analytics-properties';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { getUserTypeCookie } from '~/features/cookies/utils/get-user-type-cookie';
import { publicEnv } from '~/features/environment/public';
import { useExperiment } from '~/features/experiments/hooks/use-experiment';
import { removeExperimentRootFromUrl } from '~/features/experiments/utils';
import type { Page } from '~/features/page/models/page';
import { useClientRender } from '~/hooks/use-client-render';

const segmentWriteKey = publicEnv.NEXT_PUBLIC_SEGMENT_WRITE_KEY;
const environmentName = publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME;
const qualifiedKey = publicEnv.NEXT_PUBLIC_QUALIFIED_KEY;

const faviconTags: LinkTag[] = [
  {
    rel: 'icon',
    href: '/favicon/favicon.ico',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '32x32',
    href: '/favicon/favicon-32x32.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '16x16',
    href: '/favicon/favicon-16x16.png',
  },
  {
    rel: 'apple-touch-icon',
    href: '/favicon/apple-touch-icon.png',
    sizes: '180x180',
  },
  {
    rel: 'manifest',
    href: '/favicon/site.webmanifest',
  },
];

export function PageHead({
  page,
  seoProps,
  pageCategory,
  nextHeadChildren,
  forcedCanonicalPath,
  additionalPageAnalytics,
}: {
  page: Page;
  pageCategory?: PageCategory;
  nextHeadChildren?: ReactNode;
  forcedCanonicalPath?: string;
  additionalPageAnalytics?: Record<string, unknown>;
  seoProps: NextSeoProps;
}) {
  const isClient = useClientRender();
  const router = useRouter();
  const pageAnalytics = useMemo(() => {
    return {
      ...additionalPageAnalytics,
      name: page.eventName,
      site_identifier: 'udacity-web',
      user_status: auth.isAuthenticated() ? 'logged_in' : 'logged_out',
      user_has_active_subscription: getActiveSubscriptionCookie() ? true : false, // Cookie value can be undefined
      version: '2',
      category: pageCategory ?? PageCategory.Uncategorized,
      ...getGeoAnalyticsProperties(),
    };
  }, [page, pageCategory, additionalPageAnalytics]);
  const isEnterpriseUser = isClient && getUserTypeCookie() === 'enterprise';

  if (!seoProps.canonical) {
    const url = removeExperimentRootFromUrl(new URL(`https://www.udacity.com${forcedCanonicalPath || router.asPath}`));
    // Note: we need to remove the hash and query params from the URL since it is not used for canonical url.
    url.hash = '';
    url.search = '';
    // Note: we need to add a canonical URL for pages if it isn't already defined in Sanity since we deploy our project to https://allaccess.web.udacity.com/ and these pages cannot be noindex/nofollow'd.
    seoProps.canonical = url.toString();
  }

  useEffect(() => {
    function routeChangeCompleteHandler() {
      if (typeof window !== 'undefined') {
        // Qualified
        if (typeof window.qualified !== 'undefined') {
          window.qualified('page');
        }
        // Segment Page Event
        if (typeof window.analytics !== 'undefined') {
          window.analytics.page(pageAnalytics?.category, pageAnalytics.name, pageAnalytics);
        }
      }
    }
    router.events.on('routeChangeComplete', routeChangeCompleteHandler);
    return () => {
      router.events.off('routeChangeComplete', routeChangeCompleteHandler);
    };
  }, [router, pageAnalytics]);

  const experiment = useExperiment();
  // Note: school_lp_redesign is used to force the new school lp design for new school pages while legacy school pages use the old design.
  // Will remove once _all_ school pages are using the new design.
  const isExperimentalPage =
    (router.pathname.startsWith('/test') || router.pathname.startsWith('/flag')) &&
    experiment?.school_lp_redesign !== 'on';

  return (
    <>
      <NextSeo
        {...seoProps}
        additionalLinkTags={faviconTags}
        {...(isExperimentalPage && {
          nofollow: true,
          noindex: true,
        })}
      />
      <NextHead>
        <link key='sgmt' href='https://sgmt.udacity.com' rel='preconnect' />
        <link key='transcend' href='https://transcend-cdn.com' rel='preconnect' />
        {/* Note: the following disables all shopping features for enterprise users on Microsoft Edge*/}
        {isEnterpriseUser && <meta content='disable' name='msapplication-shopping' />}
        {nextHeadChildren}
      </NextHead>

      {/* qualifiedKey is set to false in all envs except production and preview */}
      {qualifiedKey !== 'false' && page?.loadChatbotScript && (
        <>
          <Script
            id='qualified-init'
            dangerouslySetInnerHTML={{
              __html: `
              (function(w,q){w['QualifiedObject']=q;w[q]=w[q]||function(){
                (w[q].q=w[q].q||[]).push(arguments)};})(window,'qualified')
              `,
            }}
          />
          <Script async={true} id='qualified-js' src={`https://js.qualified.com/qualified.js?token=${qualifiedKey}`} />
        </>
      )}

      {/* segmentWriteKey is set to false in all envs except production and staging */}
      {typeof segmentWriteKey === 'string' && (
        <Script
          id='segment'
          strategy='afterInteractive'
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
              !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://sgmt.udacity.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey="${segmentWriteKey}";;analytics.SNIPPET_VERSION="4.15.3";
              analytics.load("${segmentWriteKey}");
              analytics.page("${pageAnalytics?.category}", "${pageAnalytics?.name}", ${JSON.stringify(pageAnalytics)});
              if (${
                publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME !== 'production' &&
                Boolean(publicEnv.NEXT_PUBLIC_DEBUG_ANALYTICS)
              }){
                console.log("page:", ${JSON.stringify(pageAnalytics)});
              }
              }}();
            `,
          }}
        />
      )}
    </>
  );
}
